import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useAdicionarAoPipeline } from '@/hooks/useGestaoLicitacoes';
import type { EmpresaListItem } from '@/types/empresa';
import type { LicitacaoFeed } from '@/hooks/useLicitacoes';

interface AdicionarLicitacaoPipelineModalProps {
  licitacao: LicitacaoFeed | null;
  empresas: EmpresaListItem[];
  onClose: () => void;
}

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-success/50 focus:border-ds-success/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200';

function extractErrorMessage(error: unknown): string {
  const err = error as { error?: { message?: string }; message?: string } | null;
  return err?.error?.message || err?.message || 'Não foi possível adicionar a licitação.';
}

function AdicionarLicitacaoPipelineModal({ licitacao, empresas, onClose }: AdicionarLicitacaoPipelineModalProps) {
  const [empresaId, setEmpresaId] = useState('');
  const empresaIdNum = parseInt(empresaId, 10) || undefined;
  const adicionar = useAdicionarAoPipeline(empresaIdNum);

  useEffect(() => {
    setEmpresaId('');
    adicionar.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licitacao?.id]);

  if (!licitacao) return null;

  return (
    <Modal isOpen onClose={onClose} title="Adicionar à gestão de licitações">
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium text-ds-text-primary">{licitacao.objeto}</p>
          {licitacao.orgao && <p className="text-xs text-ds-text-secondary mt-0.5">{licitacao.orgao}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-ds-text-secondary mb-2" htmlFor="pipeline-direto-empresa">
            Cliente
          </label>
          <select
            id="pipeline-direto-empresa"
            className={selectClassName}
            value={empresaId}
            onChange={(e) => {
              setEmpresaId(e.target.value);
              adicionar.reset();
            }}
          >
            <option value="">Selecione um cliente</option>
            {empresas.map((empresa) => (
              <option key={empresa.id} value={empresa.id}>{empresa.razao_social}</option>
            ))}
          </select>
        </div>

        {adicionar.isError && (
          <p className="text-sm text-ds-danger">{extractErrorMessage(adicionar.error)}</p>
        )}

        {adicionar.isSuccess ? (
          <div className="rounded-lg border border-ds-success/20 bg-ds-success/10 p-3 space-y-2">
            <p className="text-sm text-ds-success">Licitação adicionada ao pipeline.</p>
            <Link
              to={`/gestao-licitacoes?empresa=${empresaIdNum}`}
              className="text-xs font-medium text-ds-success hover:underline"
              onClick={onClose}
            >
              Ver na Gestão de Licitações
            </Link>
          </div>
        ) : (
          <Button
            type="button"
            onClick={() => empresaIdNum && adicionar.mutate(licitacao.id)}
            disabled={!empresaIdNum}
            isLoading={adicionar.isPending}
          >
            Adicionar ao pipeline
          </Button>
        )}
      </div>
    </Modal>
  );
}

export { AdicionarLicitacaoPipelineModal };
